import { apiRequest, type ApiError } from '@/core/http/apiClient';
import { download, print } from '@/core/http/files';

/** `Idempotency-Key` value: a UUID where available (Angular had three identical copies of this). */
export function newIdempotencyKey(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * Blob requests return their problem details as a Blob. Parses it (null when it is not JSON) and rethrows an error of
 * the same shape whose `.error` is the parsed body, so `errorMessage()` can read it. Anything else is rethrown as is.
 * Only the services that used Angular's private `readBlobError` call this; the rest deliberately do not (D5).
 */
export async function readBlobError(error: unknown): Promise<never> {
  const e = error as Partial<ApiError> | null;
  if (e?.name === 'HttpErrorResponse' && e.error instanceof Blob) {
    const text = await e.error.text();
    let body: unknown;
    try {
      body = text ? JSON.parse(text) : null;
    } catch {
      body = null;
    }
    throw { ...e, error: body } as ApiError;
  }
  throw error;
}

/** POST that returns a file: printed when `isPrint`, otherwise downloaded. Resolves true. */
export async function exportFile(
  path: string,
  body: unknown,
  isPrint: boolean,
  options: { unwrapBlobErrors?: boolean } = {},
): Promise<boolean> {
  try {
    const response = await apiRequest<Blob>('POST', path, { body, responseType: 'blob' });
    if (isPrint) {
      print(response.body ?? new Blob());
    } else {
      download(response);
    }
    return true;
  } catch (error) {
    if (options.unwrapBlobErrors) {
      return readBlobError(error);
    }
    throw error;
  }
}

/** POST that always downloads (Angular `postFile` + `save`). Resolves true. */
export async function postFile(
  path: string,
  body: unknown,
  options: { unwrapBlobErrors?: boolean } = {},
): Promise<boolean> {
  return exportFile(path, body, false, options);
}
